import { createAdminMessage, SB } from '@play-co/replicant';
import createMessages from '../config/messages/createMessages';
import {
  match2Boosters,
  Match2BoosterType,
  match2MaxLevel,
  match2Powerups,
  Match2PowerupType,
} from '../match2/match2.ruleset';
import { getMatch2TimedRewardRemainingTime } from '../match2/match2.getters';
import {
  match2Multipliers,
  Match2TimedRewardType,
  MultiplierType,
} from '../match2/rewards/match2-rewards.ruleset';
import { getDummyAPI } from '../inventory/inventory.utils';
import { clamp } from '../utils';
import { addFriend, removeFriend } from '../friends/friends.modifiers';
import { shopBundlesPacks } from '../match2/shopbundles/match2-shopbundles.ruleset';
import { gemsRewardTable, ProductId } from '../iap/iap.ruleset';
import { isPetStarterPackProduct } from '../starterpack/starterpack.getters';
import {
  BundleSaleProductId,
  bundleSaleProducts,
} from '../match2/bundlesale/match2-bundlesale.ruleset';
import {
  match2BundleSaleGetReimbursableProducts,
  match2BundleSaleIsFreeProduct,
} from '../match2/bundlesale/match2-bundlesale.getters';
import * as Inventory from '../inventory';

const salePackProductIds = Object.keys(shopBundlesPacks) as [
  ProductId,
  ...ProductId[],
];

const paidBundleSaleProductIds = bundleSaleProducts.filter(
  (productId) =>
    productId !== 'buruburu_booster_sale' &&
    !match2BundleSaleIsFreeProduct(productId) &&
    match2BundleSaleGetReimbursableProducts(productId).length > 0,
) as [BundleSaleProductId, ...BundleSaleProductId[]];

export default createMessages({
  /**
   * Add coins over admin message
   */
  admin_addCoins: createAdminMessage(
    SB.object({ amount: SB.int().min(0), reason: SB.string() }),
    (state, args, info) => {
      // Log reimbursement so the user can claim it manually.
      state.admin.push({
        coins: args.amount,
        freeGems: 0,

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Add Coins',
  ),

  /**
   * Remove coins over admin message
   */
  admin_removeCoins: createAdminMessage(
    SB.object({ amount: SB.int().min(0), reason: SB.string() }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.
      const points = Inventory.getters.getPoints(state);
      const remove = Math.min(points, args.amount);

      Inventory.modifiers.consumePoints(
        state,
        remove,
        {
          feature: 'admin_message',
        },
        getDummyAPI(), // No analytics will be sent
      );

      // Log penalty as claimed.
      state.admin.push({
        coins: -args.amount,
        freeGems: 0,

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Remove Coins',
  ),

  /**
   * Set coins over admin message
   */
  admin_setCoins: createAdminMessage(
    SB.object({ amount: SB.int().min(0), reason: SB.string() }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.
      Inventory.modifiers.setPoints(state, Math.max(0, args.amount));

      // Log penalty as claimed.
      state.admin.push({
        coins: args.amount,
        freeGems: 0,

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Set Coins',
  ),

  /**
   * Add free gems over admin message
   */
  admin_addFreeGems: createAdminMessage(
    SB.object({ amount: SB.int().min(0), reason: SB.string() }),
    (state, args, info) => {
      // Log reimbursement so the user can claim it manually.
      state.admin.push({
        coins: 0,
        freeGems: args.amount,

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Add Free Gems',
  ),

  /**
   * Remove gems over admin message
   */
  admin_removeGems: createAdminMessage(
    SB.object({ amount: SB.int().min(0), reason: SB.string() }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.
      const gems = Inventory.getters.getGems(state);
      const remove = Math.min(gems, args.amount);

      Inventory.modifiers.consumeGems(
        state,
        remove,
        { feature: 'admin_message' },
        getDummyAPI(), // No analytics will be sent
      );

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: -args.amount,

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Remove Gems',
  ),

  /**
   * Set free gems over admin message
   */
  admin_setFreeGems: createAdminMessage(
    SB.object({ amount: SB.int().min(0), reason: SB.string() }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.
      Inventory.modifiers.setFreeGems(state, Math.max(0, args.amount));

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: args.amount,

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Set Free gems',
  ),

  /**
   * Rename pet over admin  message
   */
  admin_renamePet: createAdminMessage(
    SB.object({ name: SB.string(), reason: SB.string() }),
    (state, args, info) => {
      const previousPetName = state.pet.name;

      // Rename pet immediately so the user cannot avoid it.
      state.pet.name = args.name;

      // Log as unclaimed to show info.
      state.admin.push({
        coins: 0,
        freeGems: 0,
        previousPetName,
        newPetName: args.name,

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Rename Pet',
  ),

  /**
   * @deprecated
   */
  admin_decorationRecovery: createAdminMessage(
    SB.object({ timestamp: SB.number() }),
    () => {},
    'Decoration migration @deprecated',
  ),

  // ===== M2 Migration Admin Messages (feature removed) =====

  /**
   * @deprecated M2 migration feature has been removed.
   * Assign m2migration moreThan10k bucket.
   */
  admin_assignToMoreThan10kM2MigrationBucket: createAdminMessage(
    SB.object({ ok: SB.number() }),
    () => {},
    'M2Migration > 10K @deprecated',
  ),

  /**
   * @deprecated M2 migration feature has been removed.
   * Assign m2migration lessThan10K bucket.
   */
  admin_assignToLessThan10KM2MigrationBucket: createAdminMessage(
    SB.object({ ok: SB.number().default(1) }),
    () => {},
    'M2Migration < 10K @deprecated',
  ),

  /**
   * @deprecated M2 migration feature has been removed.
   * Assign m2migration default bucket.
   */
  admin_assignToDefault2MigrationBucket: createAdminMessage(
    SB.object({ ok: SB.number().default(1) }),
    () => {},
    'M2Migration default @deprecated',
  ),

  /**
   * @deprecated M2 migration feature has been removed.
   * Assign m2migration compensation bucket 2501_5000_FreeGems.
   */
  admin_assignCompensationBucket_2501_5000_FreeGems: createAdminMessage(
    SB.object({ ok: SB.number() }),
    () => {},
    '2,501 - 5,000 FreeGems @deprecated',
  ),

  /**
   * @deprecated M2 migration feature has been removed.
   * Assign m2migration compensation bucket 5001_10000_FreeGems.
   */
  admin_assignCompensationBucket_5001_10000_FreeGems: createAdminMessage(
    SB.object({ ok: SB.number().default(1) }),
    () => {},
    '5,001 - 10,000 Free Gems @deprecated',
  ),

  /**
   * @deprecated M2 migration feature has been removed.
   * Assign m2migration compensation bucket 10_001_20000_FreeGems.
   */
  admin_assignCompensationBucket_10001_20000_FreeGems: createAdminMessage(
    SB.object({ ok: SB.number().default(1) }),
    () => {},
    '10,001 - 20000 Free Gems @deprecated',
  ),

  /**
   * @deprecated M2 migration feature has been removed.
   * Assign m2migration compensation bucket 20001_FreeGems.
   */
  admin_assignCompensationBucket_20001_FreeGems: createAdminMessage(
    SB.object({ ok: SB.number().default(1) }),
    () => {},
    '20,001 + Free Gems @deprecated',
  ),

  /**
   * @deprecated M2 migration feature has been removed.
   * Assign m2migration compensation bucket 10k_PaidGems.
   */
  admin_assignCompensationBucket_10k_PaidGems: createAdminMessage(
    SB.object({ ok: SB.number().default(1) }),
    () => {},
    '10k PaidGems @deprecated',
  ),

  /**
   * Add powerups over admin message
   */
  admin_addPowerups: createAdminMessage(
    SB.object({
      type: SB.tuple(match2Powerups),
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as Match2PowerupType;

      if (!match2Powerups.includes(type)) {
        console.warn(`Invalid powerup type: ${args.type}`);
        return;
      }

      // Log reimbursement so the user can claim it manually.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'powerups',
          subType: type,
          amount: args.amount ?? 0,
          duration: args.duration ?? 0,
        },

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Add Powerups',
  ),

  /**
   * Remove powerups over admin message
   */
  admin_removePowerups: createAdminMessage(
    SB.object({
      type: SB.tuple(match2Powerups),
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as Match2PowerupType;

      if (!match2Powerups.includes(type)) {
        console.warn(`Invalid powerup type: ${args.type}`);
        return;
      }

      // Claim penalty immediately so the user cannot avoid it.

      if (args.amount) {
        const remaining = state.match2.powerups[type] ?? 0;
        const remove = Math.min(remaining, args.amount);

        state.match2.powerups[type] = remaining - remove;
      }

      if (args.duration) {
        const key = `powerups-${type}` as Match2TimedRewardType;
        const remaining = getMatch2TimedRewardRemainingTime(
          state,
          key,
          info.timestamp,
        );
        const remove = Math.min(remaining, args.duration);
        const duration = remaining - remove;
        const { setMatch2TimedReward } = require('../match2/match2.modifiers');
        setMatch2TimedReward(state, info.timestamp, type, duration);
      }

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'powerups',
          subType: type,
          amount: args.amount ? -args.amount : 0,
          duration: args.duration ? -args.duration : 0,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Remove Powerups',
  ),

  /**
   * Set powerups over admin message
   */
  admin_setPowerups: createAdminMessage(
    SB.object({
      type: SB.tuple(match2Powerups),
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as Match2PowerupType;

      if (!match2Powerups.includes(type)) {
        console.warn(`Invalid powerup type: ${args.type}`);
        return;
      }

      // Claim penalty immediately so the user cannot avoid it.

      if (args.amount) {
        state.match2.powerups[type] = args.amount;
      }

      if (args.duration) {
        const key = `powerups-${type}` as Match2TimedRewardType;
        const { setMatch2TimedReward } = require('../match2/match2.modifiers');
        setMatch2TimedReward(state, info.timestamp, key, args.duration);
      }

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'powerups',
          subType: type,
          amount: args.amount ?? 0,
          duration: args.duration ?? 0,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Set Powerups',
  ),

  /**
   * Add boosters over admin message
   */
  admin_addBoosters: createAdminMessage(
    SB.object({
      type: SB.tuple(match2Boosters),
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as Match2BoosterType;

      if (!match2Boosters.includes(type)) {
        console.warn(`Invalid booster type: ${args.type}`);
        return;
      }

      // Log reimbursement so the user can claim it manually.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'boosters',
          subType: type,
          amount: args.amount ?? 0,
          duration: args.duration ?? 0,
        },

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Add Boosters',
  ),

  /**
   * Remove boosters over admin message
   */
  admin_removeBoosters: createAdminMessage(
    SB.object({
      type: SB.tuple(match2Boosters),
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as Match2BoosterType;

      if (!match2Boosters.includes(type)) {
        console.warn(`Invalid booster type: ${args.type}`);
        return;
      }

      // Claim penalty immediately so the user cannot avoid it.

      if (args.amount) {
        const remaining = state.match2.boosters[type] ?? 0;
        const remove = Math.min(remaining, args.amount);

        state.match2.boosters[type] = remaining - remove;
      }

      if (args.duration) {
        const key = `boosters-${type}` as Match2TimedRewardType;
        const remaining = getMatch2TimedRewardRemainingTime(
          state,
          key,
          info.timestamp,
        );
        const remove = Math.min(remaining, args.duration);
        const duration = remaining - remove;
        const { setMatch2TimedReward } = require('../match2/match2.modifiers');
        setMatch2TimedReward(state, info.timestamp, type, duration);
      }

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'boosters',
          subType: type,
          amount: args.amount ? -args.amount : 0,
          duration: args.duration ? -args.duration : 0,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Remove Boosters',
  ),

  /**
   * Set boosters over admin message
   */
  admin_setBoosters: createAdminMessage(
    SB.object({
      type: SB.tuple(match2Boosters),
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as Match2BoosterType;

      if (!match2Boosters.includes(type)) {
        console.warn(`Invalid booster type: ${args.type}`);
        return;
      }

      // Claim penalty immediately so the user cannot avoid it.

      if (args.amount) {
        state.match2.boosters[type] = args.amount;
      }

      if (args.duration) {
        const key = `boosters-${type}` as Match2TimedRewardType;
        const { setMatch2TimedReward } = require('../match2/match2.modifiers');
        setMatch2TimedReward(state, info.timestamp, key, args.duration);
      }

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'boosters',
          subType: type,
          amount: args.amount ?? 0,
          duration: args.duration ?? 0,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Set Boosters',
  ),

  /**
   * Add energy over admin message
   */
  admin_addEnergy: createAdminMessage(
    SB.object({
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      // Log reimbursement so the user can claim it manually.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'energy',
          amount: args.amount ?? 0,
          duration: args.duration ?? 0,
        },

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Add Energy',
  ),

  /**
   * Remove energy over admin message
   */
  admin_removeEnergy: createAdminMessage(
    SB.object({
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.

      if (args.amount) {
        const energy = Inventory.getters.getEnergy(state, info.timestamp);
        const remove = Math.min(energy, args.amount);

        Inventory.modifiers.consumeEnergy(
          state,
          remove,
          info.timestamp,
          { feature: 'admin_message' },
          getDummyAPI(), // No analytics will be sent
        );
      }

      if (args.duration) {
        const remaining = getMatch2TimedRewardRemainingTime(
          state,
          'energy',
          info.timestamp,
        );
        const remove = Math.min(remaining, args.duration);
        const duration = remaining - remove;
        const { setMatch2TimedReward } = require('../match2/match2.modifiers');
        setMatch2TimedReward(state, info.timestamp, 'energy', duration);
      }

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'energy',
          amount: args.amount ? -args.amount : 0,
          duration: args.duration ? -args.duration : 0,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Remove Energy',
  ),

  /**
   * Set energy over admin message
   */
  admin_setEnergy: createAdminMessage(
    SB.object({
      amount: SB.int().min(0).optional(),
      duration: SB.int().min(0).optional(),
      reason: SB.string(),
    }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.

      if (args.amount) {
        Inventory.modifiers.setEnergy(state, args.amount, info.timestamp);
      }

      if (args.duration) {
        const { setMatch2TimedReward } = require('../match2/match2.modifiers');
        setMatch2TimedReward(state, info.timestamp, 'energy', args.duration);
      }

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'energy',
          amount: args.amount ?? 0,
          duration: args.duration ?? 0,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Set Energy',
  ),

  /**
   * Add extra moves over admin message
   */
  admin_addMoves: createAdminMessage(
    SB.object({
      amount: SB.int().min(0),
      reason: SB.string(),
    }),
    (state, args, info) => {
      // Log reimbursement so the user can claim it manually.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'moves',
          amount: args.amount,
        },

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Add Extra Moves',
  ),

  /**
   * Remove extra moves over admin message
   */
  admin_removeMoves: createAdminMessage(
    SB.object({
      amount: SB.int().min(0),
      reason: SB.string(),
    }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.
      const moves = state.match2.inventory.extraMoves;
      const remove = Math.min(moves, args.amount);
      state.match2.inventory.extraMoves = moves - remove;

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'moves',
          amount: -args.amount,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Remove Extra Moves',
  ),

  /**
   * Set extra moves over admin message
   */
  admin_setMoves: createAdminMessage(
    SB.object({
      amount: SB.int().min(0),
      reason: SB.string(),
    }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.
      state.match2.inventory.extraMoves = args.amount;

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'moves',
          amount: args.amount,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Set Extra Moves',
  ),

  /**
   * Add multiplier over admin message
   */
  admin_addMultiplier: createAdminMessage(
    SB.object({
      type: SB.string(), // TODO: Change to SB.tuple(match2Multipliers) when tuple type is supported
      duration: SB.int().min(0),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as MultiplierType;

      if (!match2Multipliers.includes(type)) {
        console.warn(`Invalid multiplier type: ${args.type}`);
        return;
      }

      // Log reimbursement so the user can claim it manually.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'multiplier',
          subType: type,
          duration: args.duration,
        },

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Add Multiplier',
  ),

  /**
   * Remove multiplier over admin message
   */
  admin_removeMultiplier: createAdminMessage(
    SB.object({
      type: SB.string(), // TODO: Change to SB.tuple(match2Multipliers) when tuple type is supported
      duration: SB.int().min(0),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as MultiplierType;

      if (!match2Multipliers.includes(type)) {
        console.warn(`Invalid multiplier type: ${args.type}`);
        return;
      }

      // Claim penalty immediately so the user cannot avoid it.
      const key = `multiplier-${type}` as Match2TimedRewardType;
      const remaining = getMatch2TimedRewardRemainingTime(
        state,
        key,
        info.timestamp,
      );
      const remove = Math.min(remaining, args.duration);
      const duration = remaining - remove;
      const { setMatch2TimedReward } = require('../match2/match2.modifiers');
      setMatch2TimedReward(state, info.timestamp, key, duration);

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'multiplier',
          subType: type,
          duration: -args.duration,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Remove Multiplier',
  ),

  /**
   * Set multiplier over admin message
   */
  admin_setMultiplier: createAdminMessage(
    SB.object({
      type: SB.string(), // TODO: Change to SB.tuple(match2Multipliers) when tuple type is supported
      duration: SB.int().min(0),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const type = args.type as MultiplierType;

      if (!match2Multipliers.includes(type)) {
        console.warn(`Invalid multiplier type: ${args.type}`);
        return;
      }

      // Claim penalty immediately so the user cannot avoid it.
      const key = `multiplier-${type}` as Match2TimedRewardType;
      const { setMatch2TimedReward } = require('../match2/match2.modifiers');
      setMatch2TimedReward(state, info.timestamp, key, args.duration);

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        match2Consumables: {
          type: 'multiplier',
          subType: type,
          duration: args.duration,
        },

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Set Multiplier',
  ),

  /**
   * Grant current battle pass over admin message
   */
  admin_grantBattlePass: createAdminMessage(
    SB.object({ reason: SB.string() }),
    (state, args, info) => {
      // Log reimbursement so the user can claim it manually.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        battlePass: true,

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Grant Battle Pass',
  ),

  /**
   * Revoke current battle pass over admin message
   */
  admin_revokeBattlePass: createAdminMessage(
    SB.object({ reason: SB.string() }),
    (state, args, info) => {
      // Claim penalty immediately so the user cannot avoid it.
      if (state.match2.battlePass) {
        state.match2.battlePass.purchased = false;
      }

      // Log penalty as claimed.
      state.admin.push({
        coins: 0,
        freeGems: 0,

        battlePass: false,

        reason: args.reason,
        claimed: true,

        timestamp: info.timestamp,
      });
    },
    'Revoke Battle Pass',
  ),

  /**
   * Clear duo invitations
   */
  admin_clearDuoInvitations: createAdminMessage(
    SB.object({ reason: SB.string() }),
    (state, args, info) => {
      // Clear last duo response
      delete state.duo.debut;
      delete state.duo.lastResponse;
    },
    'Clear Duo Invitations',
  ),

  /**
   * Super lollipop - Recover Streak
   */
  admin_recoverSuperLollipopStreak: createAdminMessage(
    SB.object({ streak: SB.int().min(0), reason: SB.string() }),
    (state, args, info) => {
      // User must have super lollipop feature to be able to recover streak
      if (!state.superLollipop) {
        return;
      }

      // Push an unclaimed message, so user can see it in the next launch
      state.admin.push({
        reason: args.reason,
        claimed: false,
        timestamp: info.timestamp,
        superLollipopStreak: args.streak,
      });
    },
    'Super Lollipop - Recover Streak',
  ),

  /**
   * @deprecated Fairy Forest feature has been removed (#11760).
   * Kept registered as a NOOP so the CS tool entry does not fail.
   */
  admin_fairyForestAddBalls: createAdminMessage(
    SB.object({ balls: SB.int().min(0), reason: SB.string() }),
    () => {},
    'Fairy Forest - Add Balls @deprecated',
  ),

  /**
   * Puzzle - Set Level
   */
  admin_setPuzzleLevel: createAdminMessage(
    SB.object({ puzzleLevel: SB.int().min(0), reason: SB.string() }),
    (state, args, info) => {
      // Just set the level straight away, no need to push an admin message
      state.match2.level = clamp(args.puzzleLevel, 1, match2MaxLevel + 1);
    },
    'Puzzle - Set Level',
  ),

  /**
   * Demographics - Set age range
   *
   * Overrides demographics information in GCInstant's platform storage. Used to correct players with incorrect demographics information.
   */
  admin_setAgeRange: createAdminMessage(
    SB.object({ min: SB.int() }),
    (state, args) => {
      const demographics = state.platformStorage?.demographics;

      const isValidDemographics =
        demographics && typeof demographics === 'object';

      if (!isValidDemographics) {
        return;
      }

      (demographics as Record<string, unknown>).ageRange = { min: args.min };
    },
    'Demographics - Set age range',
  ),

  /**
   * Friendship - Add a friend if not already exists, and remove any friendship requests to/from a given user ID.
   */
  admin_addFriend: createAdminMessage(
    SB.object({ id: SB.string(), reason: SB.string() }),
    (state, args, info) => {
      // Add friend if not already exists
      addFriend(state, args.id, info.timestamp);

      // Remove friendship requests to/from that user if exist
      delete state.friendshipRequests[args.id];
    },
    'Friendship - Add Friend',
  ),

  /**
   * Friendship - Remove a friend and friendship request from/to a given user ID, if they exist.
   */
  admin_removeFriend: createAdminMessage(
    SB.object({ id: SB.string(), reason: SB.string() }),
    (state, args) => {
      // Remove friend if exists
      removeFriend(state, args.id);

      // Remove friendship requests to/from that user if exist
      delete state.friendshipRequests[args.id];
    },
    'Friendship - Remove Friend',
  ),

  /**
   * Grant a complete sale pack over admin message.
   * Grants paid gems + bonus items (powerups, boosters, energy).
   * Sets starter pack flags if applicable.
   * Use this when a player paid but didn't receive their pack.
   */
  admin_grantSalePack: createAdminMessage(
    SB.object({
      productId: SB.tuple(salePackProductIds),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const productId = args.productId as ProductId;

      // Validate product ID exists in shop bundles
      const pack = shopBundlesPacks[productId];
      if (!pack) {
        console.warn(`Invalid sale pack product ID: ${args.productId}`);
        return;
      }

      // Get gem rewards from ruleset
      const gemRewards = gemsRewardTable[productId];
      const paidGems = gemRewards?.paidGems ?? 0;
      const freeGems = gemRewards?.freeGems ?? 0;

      // Determine which starter pack flags to set
      const isStarterPack = productId === 'buruburu_starter_pack';
      const isPetStarterPack = isPetStarterPackProduct(productId);

      // Log grant so the user can claim it manually
      state.admin.push({
        coins: 0,
        freeGems: 0,

        salePack: {
          productId: args.productId,
          paidGems,
          freeGems,
          setStarterPackPurchased: isStarterPack,
          setPetStarterPackPurchased: isPetStarterPack,
        },

        reason: args.reason,
        claimed: false,

        timestamp: info.timestamp,
      });
    },
    'Grant Sale Pack',
  ),

  /**
   * Grant bundle sale paid product and its subsequent free products over admin message.
   * Use this when a player paid but didn't receive booster sale pack rewards.
   */
  admin_grantBundleSalePack: createAdminMessage(
    SB.object({
      productId: SB.tuple(paidBundleSaleProductIds),
      reason: SB.string(),
    }),
    (state, args, info) => {
      const productId = args.productId as BundleSaleProductId;

      if (!bundleSaleProducts.includes(productId)) {
        console.warn(`Invalid bundle sale product ID: ${args.productId}`);
        return;
      }

      if (match2BundleSaleIsFreeProduct(productId)) {
        console.warn(`Bundle sale product must be paid: ${args.productId}`);
        return;
      }

      const reimbursableProductIds =
        match2BundleSaleGetReimbursableProducts(productId);
      if (reimbursableProductIds.length === 0) {
        console.warn(
          `Failed to derive reimbursable bundle sale products: ${args.productId}`,
        );
        return;
      }

      const gemRewards = gemsRewardTable[productId as ProductId];
      const paidGems = gemRewards?.paidGems ?? 0;
      const freeGems = gemRewards?.freeGems ?? 0;

      state.admin.push({
        coins: 0,
        freeGems: 0,

        bundleSalePack: {
          paidProductId: args.productId,
          reimbursableProductIds,
          paidGems,
          freeGems,
        },

        reason: args.reason,
        claimed: false,
        timestamp: info.timestamp,
      });
    },
    'Grant Bundle Sale Pack',
  ),
});
